import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect, Option } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import type { PaymentRequestAdapters } from '../../../module/order/orderAdapters';
import type {
  OrderPaymentId,
  PaymentMethodCode,
} from '../../../service/orderShipment/OrderShipment';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/payment-requests';
const HTTP_METHOD = 'post';

export type CreatePaymentRequestPayload = {
  paymentId: OrderPaymentId;
  paymentMethodCode: PaymentMethodCode;
  action: string;
  payload: {
    langCode: string;
  };
};

export type MakeCreatePaymentRequestForOrderDeps<PRType> = {
  readonly paymentRequestExtended: Schema.Codec<PRType, unknown>;
  readonly paymentRequestAdapters: PaymentRequestAdapters<StripJsonLd<PRType>>;
};

export const makeCreatePaymentRequestForOrder = <PRType>(
  deps: MakeCreatePaymentRequestForOrderDeps<PRType>
) => {
  const CreatePaymentRequestForOrderError = createBackendApiError({
    domain: 'Order',
    endpoint: 'CreatePaymentRequestForOrder',
    path: ENDPOINT,
    identifierField: 'tokenValue',
  } as const);

  const postPaymentRequestFetch = (tokenValue: string, data: CreatePaymentRequestPayload) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/json')
        .fetch({ params: { tokenValue }, body: data });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
          'payment.id': String(data.paymentId),
          'payment.methodCode': data.paymentMethodCode,
        },
      })
    );

  const createPaymentRequestForOrder = (
    tokenValue: string,
    paymentId: OrderPaymentId,
    orderShipmentPaymentCode: PaymentMethodCode,
    locale: string
  ) =>
    postPaymentRequestFetch(tokenValue, {
      paymentId: paymentId,
      paymentMethodCode: orderShipmentPaymentCode,
      action: 'capture',
      payload: {
        langCode: locale,
      },
    }).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(deps.paymentRequestExtended)(response).pipe(Effect.asSome),
          404: (response) =>
            Effect.fail(
              new CreatePaymentRequestForOrderError.NotFoundError({ tokenValue, response })
            ),
          422: () => Effect.succeed(Option.none<StripJsonLd<PRType>>()),
          orElse: (response) =>
            Effect.fail(
              new CreatePaymentRequestForOrderError.ApiRequestError({ tokenValue, response })
            ),
        })
      ),
      Effect.tap((result) =>
        Option.match(result, {
          onNone: () =>
            logApiOperation(
              ApiModule.PaymentRequestCreate,
              'Debug',
              `Payment request creation skipped (422) for order ${tokenValue} with payment ${String(paymentId)}`
            ),
          onSome: (paymentRequest) =>
            logApiOperation(
              ApiModule.PaymentRequestCreate,
              'Debug',
              `Successfully created payment request for order ${tokenValue} -> hash: ${deps.paymentRequestAdapters.hash(paymentRequest) ?? 'unknown'}`
            ),
        })
      ),
      Effect.withSpan('api.paymentRequest.create', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
          'payment.id': String(paymentId),
          'payment.methodCode': orderShipmentPaymentCode,
        },
      })
    );

  return { createPaymentRequestForOrder, CreatePaymentRequestForOrderError };
};
